<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'
	import type { SalesOrderCustomerMessage, SalesOrderInventoryMessage } from './sales-order'
	import type { i18n } from 'types/common'

	import {
		graphql,
		type SOLineItemHistory$result,
		type InitialLoadQuery$result,
		type SalesOrder$result,
	} from '$houdini'
	import { formatCustomerCurrency } from 'utility/customer-helper'
	import { formatISO } from 'date-fns'
	import { getContext } from 'svelte'

	import Attachments, { type BaseAttachmentFile } from '@isoftdata/svelte-attachments'
	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import Button from '@isoftdata/svelte-button'
	import Input from '@isoftdata/svelte-input'
	import MessageCard from './MessageCard.svelte'
	import NavTabBar from '@isoftdata/svelte-nav-tab-bar'
	import Select from '@isoftdata/svelte-select'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'

	type SelectedLineItem = NonNullable<SalesOrder$result['salesOrder']>['lines']['items'][number]

	interface Props {
		asr: SvelteAsr
		salesOrder: SalesOrder$result['salesOrder']
		attachments: Array<BaseAttachmentFile & { salesOrderFileId: number }>
		salesOrderTerms: InitialLoadQuery$result['salesOrderTerms']
		salesOrderCustomerMessage: SalesOrderCustomerMessage | null
		salesOrderInventoryMessage: SalesOrderInventoryMessage | null
	}

	let {
		asr,
		salesOrder,
		attachments = [],
		salesOrderTerms = [],
		salesOrderCustomerMessage = null,
		salesOrderInventoryMessage = null,
	}: Props = $props()

	let selectedTab: 'LINE_ITEM' | 'ADJUSTMENTS' | 'ATTACHMENTS' | 'MESSAGES' | 'PAYMENTS' = $state('LINE_ITEM')
	let selectedLineItem: SelectedLineItem | null = $state(null)
	let selectedLineItemDetail: 'ITEM_INFO' | 'HISTORY' | 'ECOMMERCE' = $state('ITEM_INFO')
	let selectedLineItemHistory: SOLineItemHistory$result['inventory']['allocations']['salesOrderLines']['items'] =
		$state([])

	let showLineItemDetails = $state(false)
	let isLoadingLineItemHistory = $state(false)
	let isCalculatingPaymentDueDate = $state(false)
	let salesOrderPaymentDueDate: Date | null = $state(null)

	const { t: translate } = getContext<i18n>('i18next')

	const mediator = getContext<Mediator>('mediator')

	const columns: Array<Column<SelectedLineItem>> = [
		{
			name: '',
			property: 'id', // TODO: replace this when editing is implemented
			icon: 'info-circle',
			align: 'center',
			sortType: false,
		},
		{
			name: translate('common:lookup', 'Lookup'),
			property: 'lookup',
			title: '',
		},
		{
			name: translate('common:stockNum', 'Stock #'),
			property: 'vehicle[stockNumber]',
			title: '',
			width: '6rem',
		},
		{
			name: translate('common:description', 'Description'),
			property: 'description',
			title: '',
			width: '10rem',
		},
		{
			name: translate('common:qty', 'Qty'),
			property: 'quantity',
			title: '',
			numeric: true,
			sortType: 'ALPHA_NUM',
			width: '3rem',
		},
		{
			name: translate('common:price', 'Price'),
			property: 'price',
			title: '',
			numeric: true,
			width: '6rem',
		},
		{
			name: translate('common:manufacturer', 'Manufacturer'),
			property: 'manufacturer[name]',
			title: '',
		},
		{
			name: translate('common:store', 'Store'),
			property: 'inventory[store][code]',
			title: '',
			align: 'right',
			width: '6rem',
		},
		{
			name: translate('common:availability', 'Availability'),
			property: 'inventory[quantityAvailable]',
			title: '',
			align: 'center',
			sortType: false,
		},
		{
			name: translate('common:hold', 'Hold'),
			property: 'hold',
			title: '',
			align: 'center',
			width: '3rem',
		},
		{
			name: translate('common:tax', 'Tax'),
			property: 'taxable',
			title: '',
			align: 'center',
			width: '3rem',
		},
		{
			name: translate('common:total', 'Total'),
			property: 'total',
			title: '',
			numeric: true,
			sortType: false,
			width: '6rem',
		},
		{
			name: translate('common:cost', 'Cost'),
			property: 'cost',
			title: '',
			numeric: true,
			sortType: false,
			width: '6rem',
		},
		{
			name: translate('common:locations', 'Locations'),
			property: 'locations',
			title: '',
			sortType: false,
		},
	]
	const lineItemInformationList: Array<{ id: string; label: string }> = [
		{ id: 'ITEM_INFO', label: translate('common:itemInfo', 'Item Info') },
		{ id: 'HISTORY', label: translate('common:history', 'History') },
		// { id: 'ECOMMERCE', label: 'e-Commerce' },
	]

	const calculatePaymentDueDateQuery = graphql(`
		query CalculatePaymentDueDate($termId: UInt, $documentDate: Date) {
			calculateSalesOrderDueDate(termId: $termId, documentDate: $documentDate)
		}
	`)

	const loadSOLineItemHistoryQuery = graphql(`
		query SOLineItemHistory($inventoryId: UInt!, $filter: SalesOrderLineFilter) {
			inventory(id: $inventoryId) {
				allocations {
					salesOrderLines(filter: $filter) {
						items {
							id
							quantity
							price
							salesOrder {
								date
								salesOrderId
								documentType {
									name
								}
								store {
									id
								}
							}
						}
					}
				}
			}
		}
	`)

	function getCoreStatusMessage(
		lineItemType: SelectedLineItem['type'],
		coreStatus: SelectedLineItem['coreStatus'],
		quantityReturned: SelectedLineItem['quantityReturned'],
		inventoryId?: number,
	): string {
		if (lineItemType === 'INHERENT_CORE') {
			if (coreStatus === 'REJECTED') {
				return translate('common:discarded', 'Discarded')
			}
			return parseInt(quantityReturned, 10) > 0
				? translate('common:credited', 'Credited')
				: translate('common:outstanding', 'Outstanding')
		}
		if (lineItemType === 'DIRTY_CORE') {
			if (coreStatus === 'REJECTED') {
				return translate('common:discarded', 'Discarded')
			}
			if (coreStatus === 'PROCESSED') {
				return inventoryId
					? `${translate('common:returned', 'Returned')} (SKU ${inventoryId})`
					: translate('common:returned', 'Returned')
			}
			if (coreStatus === 'AVAILABLE') {
				return translate('common:pending', 'Pending')
			}
		}
		return translate('common:notSlashAcceptable', 'N/A')
	}

	async function calculatePaymentDueDate() {
		if (!salesOrder?.date || !salesOrder?.term || !salesOrder?.term.id) {
			return null
		}

		isCalculatingPaymentDueDate = true
		try {
			const { data } = await calculatePaymentDueDateQuery.fetch({
				variables: {
					termId: salesOrder.term.id,
					documentDate: salesOrder.date,
				},
			})

			if (!data?.calculateSalesOrderDueDate) {
				throw new Error(translate('salesOrder.failedToCalculatePaymentDueDate', 'Failed To Calculate Payment Due Date'))
			}
			return data.calculateSalesOrderDueDate
		} catch (error: any) {
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('salesOrder.failedToCalculatePaymentDueDate', 'Failed To Calculate Payment Due Date'),
				message: error.message,
				time: false,
			})
			return null
		} finally {
			isCalculatingPaymentDueDate = false
		}
	}

	async function loadSOLineItemHistory() {
		if (!selectedLineItem || !selectedLineItem.inventory || !salesOrder || !salesOrder.customer?.id) {
			return
		}

		isLoadingLineItemHistory = true
		try {
			const { data } = await loadSOLineItemHistoryQuery.fetch({
				variables: {
					inventoryId: selectedLineItem.inventory.id,
					filter: {
						salesOrderFilter: {
							customerFilter: {
								ids: [salesOrder.customer.id],
							},
						},
					},
				},
			})

			if (!data) {
				throw new Error(
					translate(
						'salesOrder.failedToLoadSalesOrderLineItemHistory',
						'Failed to load sales order line item history.',
					),
				)
			}

			selectedLineItemHistory = data.inventory.allocations.salesOrderLines.items
		} catch (error: any) {
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('salesOrder.failedToLoadLineItemHistory', 'Failed To Load Line Item History'),
				message: error.message,
				time: false,
			})
		} finally {
			isLoadingLineItemHistory = false
		}
	}
</script>

<div class="row">
	<div class={['col-12', { 'col-md-8': showLineItemDetails || selectedTab === 'PAYMENTS' }]}>
		<div class="card h-100">
			<div class="card-header">
				<NavTabBar
					tabs={[
						{ name: 'LINE_ITEM', title: translate('common:lineItems', 'Line Items') },
						{ name: 'ADJUSTMENTS', title: translate('common:adjustments', 'Adjustments') },
						{ name: 'ATTACHMENTS', title: translate('common:attachments', 'Attachments') },
						{ name: 'MESSAGES', title: translate('salesOrder.messages', 'Messages') },
						{ name: 'PAYMENTS', title: translate('common:payments', 'Payments') },
					]}
					bind:selectedTab
					tabChange={async () => {
						showLineItemDetails = false
						if (selectedTab === 'PAYMENTS') {
							if (!salesOrderPaymentDueDate) {
								salesOrderPaymentDueDate = await calculatePaymentDueDate()
							}
						}
						asr.go(null, { selectedTab }, { inherit: true })
					}}
				/>
			</div>
			<div class="card-body">
				<!-- NOTE: each tabs here should probably be its own component, do it when working on editing -->
				{#if selectedTab === 'LINE_ITEM'}
					<Table
						responsive
						stickyHeader
						columnHidingEnabled
						columnResizingEnabled
						{columns}
						parentStyle="max-height: 30vh"
						rows={salesOrder?.lines.items || []}
					>
						{#snippet noRows({ visibleColumnsCount })}
							<tr>
								<td
									class="text-center"
									colspan={visibleColumnsCount}>{translate('salesOrder.noLineItemsFound', 'No line items found')}</td
								>
							</tr>
						{/snippet}
						{#snippet children({ row: lineItem })}
							<tr
								class="cursor-pointer"
								class:table-primary={selectedLineItem?.id === lineItem.id}
								onclick={() => {
									selectedLineItem = lineItem
									if (selectedLineItemDetail === 'HISTORY') {
										loadSOLineItemHistory()
									}
								}}
							>
								<Td property="id"></Td>
								<Td
									property="lookup"
									class="text-nowrap"
								>
									{lineItem.lookup ??
										lineItem.inventory?.partNumber ??
										lineItem.inventory?.partNumberStripped ??
										lineItem.inventory?.inventoryId}
								</Td>
								<Td property="vehicle[stockNumber]">{lineItem.vehicle?.stockNumber ?? ''}</Td>
								<Td
									property="description"
									class="text-nowrap"
								>
									{lineItem.description}
								</Td>
								<Td property="quantity">{lineItem.quantity}</Td>
								<Td
									property="price"
									class="text-nowrap"
								>
									{formatCustomerCurrency(lineItem.price)}
								</Td>
								<Td property="manufacturer[name]">{lineItem.manufacturer?.code}</Td>
								<Td property="inventory[store][code]">{lineItem.inventory?.store.code}</Td>
								<Td property="inventory[quantityAvailable]">
									{lineItem.inventory?.quantityAvailable ?? 0} / {lineItem.inventory?.quantity ?? 0}
								</Td>
								<Td
									property="hold"
									onclick={event => {
										// NOTE: There's a reason for this, but I cant remember why
										// If not, please change this to use the Td's stopPropagation callback
										event.stopPropagation()
									}}
								>
									<!-- NOTE: this will be changed to checkbox when on "edit mode" -->
									<BooleanIndicator value={!!lineItem.hold} />
								</Td>
								<Td
									property="taxable"
									onclick={event => {
										event.stopPropagation()
									}}
								>
									<BooleanIndicator value={lineItem.taxable} />
								</Td>
								<Td
									property="total"
									class="text-nowrap"
								>
									{formatCustomerCurrency(lineItem.total)}
								</Td>
								<Td
									property="cost"
									class="text-nowrap"
								>
									{formatCustomerCurrency(lineItem.cost)}
								</Td>
								<Td
									property="locations"
									class="text-nowrap"
								>
									{lineItem.locations.map(location => location.location.name).join(',')}
								</Td>
							</tr>
						{/snippet}
					</Table>
				{:else if selectedTab === 'ADJUSTMENTS'}
					<Table
						responsive
						stickyHeader
						columnHidingEnabled
						columnResizingEnabled
						parentStyle="max-height: 30vh"
						columns={[
							{ property: 'adjustmentType[name]', name: translate('salesOrder.adjustmentType', 'Adjustment Type') },
							{ property: 'amount', name: translate('common:amount', 'Amount'), title: '', numeric: true },
							{ property: 'taxable', name: translate('common:taxable', 'Taxable'), title: '', align: 'center' },
							{ property: 'description', name: translate('common:description', 'Description'), title: '' },
							{
								property: 'subtotalAdjustment',
								name: translate('salesOrder.subtotalAdjustment', 'Subtotal Adjustment'),
								align: 'center',
							},
						]}
						rows={salesOrder?.adjustments || []}
					>
						{#snippet noRows({ visibleColumnsCount })}
							<tr>
								<td
									class="text-center"
									colspan={visibleColumnsCount}>{translate('salesOrder.noAdjustmentsFound', 'No adjustments found')}</td
								>
							</tr>
						{/snippet}
						{#snippet children({ row: adjustment })}
							<tr>
								<Td property="adjustmentType[name]">{adjustment.adjustmentType.name}</Td>
								<Td property="amount">{formatCustomerCurrency(adjustment.amount)}</Td>
								<Td
									property="taxable"
									onclick={event => {
										event.stopPropagation()
									}}
								>
									<BooleanIndicator value={adjustment.taxable} />
								</Td>
								<Td property="description">{adjustment.description}</Td>
								<Td
									property="subtotalAdjustment"
									onclick={event => {
										event.stopPropagation()
									}}
								>
									<BooleanIndicator value={adjustment.subtotalAdjustment} />
								</Td>
							</tr>
						{/snippet}
					</Table>
				{:else if selectedTab === 'ATTACHMENTS'}
					<Attachments
						uploadDisabled
						bind:fileList={attachments}
					/>
				{:else if selectedTab === 'MESSAGES'}
					{#if salesOrderCustomerMessage || salesOrderInventoryMessage}
						<div
							class="row"
							style="max-height: 30vh; overflow-y: auto;"
						>
							{#if salesOrderCustomerMessage}
								<div class="col-12 col-md-3">
									<MessageCard
										headerText={translate('common:customer', 'Customer')}
										messages={salesOrderCustomerMessage}
									/>
								</div>
							{/if}
							{#if salesOrderInventoryMessage}
								{#each Object.values(salesOrderInventoryMessage) as { partNumber, messages }}
									<div class="col-12 col-md-3">
										<MessageCard
											{messages}
											headerText={`Tag #: ${partNumber}`}
										/>
									</div>
								{/each}
							{/if}
						</div>
					{:else}
						<p class="text-center">
							{translate('salesOrder.noMessagesFoundForThisSalesOrder', 'No messages found for this Sales Order.')}
						</p>
					{/if}
				{:else if selectedTab === 'PAYMENTS'}
					<Table
						responsive
						stickyHeader
						columnHidingEnabled
						columnResizingEnabled
						parentStyle="max-height: 30vh"
						columns={[
							{ property: 'payment[paymentMethod][name]', name: translate('common:type', 'Type') },
							{ property: 'amount', name: translate('common:amount', 'Amount'), numeric: true },
							{ property: 'payment[documentNumber]', name: translate('salesOrder.checkSlashAuthNum', 'Check/Auth#') },
							{ property: 'payment[id]', name: translate('salesOrder.paymentNum', 'Payment#'), align: 'right' },
							{ property: 'payment[createdBy]', name: translate('common:createdBy', 'Created By') },
							{ property: 'payment[date]', name: translate('common:createdOn', 'Created On') },
						]}
						rows={salesOrder?.appliedPaymentLines || []}
					>
						{#snippet noRows({ visibleColumnsCount })}
							<tr>
								<td
									class="text-center"
									colspan={visibleColumnsCount}
									>{translate('salesOrder.noPaymentItemFound', 'No payment item found')}</td
								>
							</tr>
						{/snippet}
						{#snippet children({ row: salesOrderPaymentLine })}
							<tr>
								<Td property="payment[paymentMethod][name]">{salesOrderPaymentLine.payment.paymentMethod.name}</Td>
								<Td property="amount">{formatCustomerCurrency(salesOrderPaymentLine.amount)}</Td>
								<Td property="payment[documentNumber]">{salesOrderPaymentLine.payment.documentNumber}</Td>
								<Td property="payment[id]">{salesOrderPaymentLine.payment.id}</Td>
								<Td property="payment[createdBy]">{salesOrderPaymentLine.payment.createdBy.name}</Td>
								<Td property="payment[date]"
									>{formatISO(salesOrderPaymentLine.payment.date, { representation: 'date' })}</Td
								>
							</tr>
						{/snippet}
					</Table>
				{/if}
			</div>
			{#if selectedTab === 'LINE_ITEM'}
				<div class="card-footer d-flex justify-content-between align-items-center">
					<div>
						<Button
							disabled
							outline
							size="sm"
							color="success"
							iconClass="plus"
						>
							{translate('salesOrder.addItem', 'Add Item')}
						</Button>
						<Button
							disabled
							outline
							class="ms-1"
							size="sm"
							color="danger"
							iconClass="trash"
						>
							{translate('common:delete', 'Delete')}
						</Button>
					</div>
					<Button
						outline
						size="sm"
						color="primary"
						disabled={!selectedLineItem?.id}
						onclick={() => {
							showLineItemDetails = !showLineItemDetails
						}}
					>
						{showLineItemDetails
							? translate('common:hideDetails', 'Hide Details')
							: translate('common:showDetails', 'Show Details')}
					</Button>
				</div>
			{:else if selectedTab === 'PAYMENTS'}
				<div class="card-footer d-flex justify-content-between align-items-center">
					<div>
						<Button
							disabled
							outline
							size="sm"
							color="success"
							iconClass="plus"
						>
							{translate('salesOrder.addPayment', 'Add Payment')}
						</Button>
						<Button
							disabled
							outline
							size="sm"
							class="ms-1"
							iconClass="pen"
						>
							{translate('common:edit', 'Edit')}
						</Button>
						<Button
							outline
							disabled
							size="sm"
							class="ms-1"
							color="danger"
							iconClass="trash"
						>
							{translate('common:delete', 'Delete')}
						</Button>
					</div>
					<Button
						outline
						disabled
						size="sm"
						color="primary"
						iconClass="credit-card"
					>
						{translate('salesOrder.manageCard', 'Manage Card')}
					</Button>
				</div>
			{/if}
		</div>
	</div>
	{#if showLineItemDetails && selectedLineItem}
		<div class="col-12 col-md-4">
			<div class="card h-100">
				<div class="card-header d-flex justify-content-between align-items-center">
					<h6 class="mb-0">
						{translate('salesOrder.lookupNum', 'Lookup #')}: {selectedLineItem.lookup ??
							selectedLineItem.inventory?.partNumber ??
							selectedLineItem.inventory?.partNumberStripped ??
							selectedLineItem.inventory?.inventoryId}
					</h6>
					<Select
						showLabel={false}
						showEmptyOption={false}
						labelParentClass="mb-0"
						bind:value={selectedLineItemDetail}
						onchange={async () => {
							if (selectedLineItemDetail === 'HISTORY') {
								await loadSOLineItemHistory()
							}
						}}
					>
						{#each lineItemInformationList as item}
							<option value={item.id}>{item.label}</option>
						{/each}
					</Select>
				</div>
				<div class="card-body">
					{#if selectedLineItemDetail === 'ITEM_INFO'}
						{@const lineItemQuantityDetails = [
							{
								label: translate('common:statusavailable', 'Available'),
								value: selectedLineItem.inventory?.quantityAvailable || '0',
							},
							{ label: translate('salesOrder.onHand', 'On Hand'), value: selectedLineItem.inventory?.quantity || '0' },
							{
								label: translate('salesOrder.onOrder', 'On Order'),
								value: selectedLineItem.inventory?.quantityOnOrder || '0',
							},
							{ label: translate('salesOrder.requested', 'Requested'), value: selectedLineItem.quantityRequested },
							{ label: translate('salesOrder.backOrder', 'Back Order'), value: selectedLineItem.backorderQuantity },
							{ label: translate('salesOrder.lostSale', 'Lost Sale'), value: selectedLineItem.lostSaleQuantity || '0' },
							{ label: translate('salesOrder.picked', 'Picked'), value: selectedLineItem.quantityPicked },
							{ label: translate('salesOrder.returned', 'Returned'), value: selectedLineItem.quantityReturned },
						]}
						<div class="row">
							{#each lineItemQuantityDetails as { label, value }}
								<div class="col-12 col-md-3">
									<Input
										readonly
										{label}
										{value}
									/>
								</div>
							{/each}
							<div class="col-12">
								<Input
									readonly
									label={translate('salesOrder.coreInfoStatus', 'Core Info/Status')}
									value={getCoreStatusMessage(
										selectedLineItem.type,
										selectedLineItem.coreStatus,
										selectedLineItem.quantityReturned,
										selectedLineItem.inventory?.inventoryId,
									)}
								/>
							</div>
						</div>
					{:else if selectedLineItemDetail === 'HISTORY'}
						<Table
							responsive
							stickyHeader
							columnHidingEnabled
							columnResizingEnabled
							parentStyle="max-height: 40vh"
							columns={[
								{
									property: 'salesOrder[store][id]',
									name: translate('common:store', 'Store'),
									align: 'right',
									width: '4rem',
								},
								{
									property: 'salesOrder[salesOrderId]',
									name: translate('salesOrder.SONum', 'SO#'),
									align: 'right',
									width: '5rem',
								},
								{ property: 'salesOrder[documentType]', name: translate('salesOrder.doc', 'Doc.'), width: '4rem' },
								{ property: 'quantity', name: translate('salesOrder.qty', 'Qty.'), align: 'right', width: '3rem' },
								{ property: 'price', name: translate('common:price', 'Price'), align: 'right', width: '5rem' },
								{ property: 'salesOrder[date]', name: translate('common:date', 'Date'), width: '6rem' },
							]}
							rows={selectedLineItemHistory}
						>
							{#snippet body({ rows: historyLines, visibleColumnsCount })}
								{#if isLoadingLineItemHistory}
									<tr>
										<td
											class="text-center"
											colspan={visibleColumnsCount}
										>
											<i class="fa fa-spinner fa-spin"></i>
											{translate('salesOrder.loadingHistory', 'Loading history')}...
										</td>
									</tr>
								{:else if historyLines.length > 0}
									{#each historyLines as historyLine}
										<tr>
											<Td property="salesOrder[store][id]">{historyLine.salesOrder.store.id}</Td>
											<Td property="salesOrder[salesOrderId]">{historyLine.salesOrder.salesOrderId}</Td>
											<Td property="salesOrder[documentType]">{historyLine.salesOrder.documentType.name}</Td>
											<Td property="quantity">{historyLine.quantity}</Td>
											<Td property="price">{formatCustomerCurrency(historyLine.price)}</Td>
											<Td property="salesOrder[date]"
												>{formatISO(historyLine.salesOrder.date, { representation: 'date' })}</Td
											>
										</tr>
									{/each}
								{:else}
									<tr>
										<td
											colspan="6"
											class="text-center">{translate('salesOrder.noHistoryFound', 'No history found')}</td
										>
									</tr>
								{/if}
							{/snippet}
						</Table>
					{/if}
				</div>
			</div>
		</div>
	{:else if selectedTab === 'PAYMENTS'}
		<div class="col-12 col-md-4">
			<div class="card h-100">
				<div class="card-header">
					<h6>{translate('salesOrder.paymentDetails', 'Payment Details')}</h6>
				</div>
				<div class="card-body">
					{#if isCalculatingPaymentDueDate}
						<div class="text-center">
							<i class="fa fa-spinner fa-spin"></i>
						</div>
					{:else}
						<div class="row">
							<div class="col-12">
								<Select
									disabled
									label="Terms"
									value={salesOrder?.term?.id || null}
								>
									{#each salesOrderTerms as term}
										<option value={term.id}>{term.name}</option>
									{/each}
								</Select>
							</div>
							<div class="col-12">
								<Input
									readonly
									label="Payment Due"
									type="date"
									value={salesOrderPaymentDueDate
										? formatISO(salesOrderPaymentDueDate, { representation: 'date' })
										: ''}
								/>
							</div>
						</div>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>
